import { useMutation, useQuery } from '@apollo/client'
import { UpdateCurrentUserCurrentFirm } from '@deltek/specification-client/core/GraphQL/Mutations/currentUserInfo.mutations'
import { CurrentUserFirmsQuery } from '@deltek/specification-client/core/GraphQL/Queries/currentUserInfo.queries'
import {
    CurrentUserFirmsProps,
    CurrentUserFirmsResponse,
} from '@deltek/specification-client/core/Models/currentUserInfo'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import InputBase from '@material-ui/core/InputBase'
import MenuItem from '@material-ui/core/MenuItem'
import Popover from '@material-ui/core/Popover'
import { makeStyles, Theme } from '@material-ui/core/styles'
import clsx from 'clsx'
import TruncatedLabel from 'components/SelectionTree/TruncatedLabel'
import React from 'react'
import { FixedSizeList } from 'react-window'
import {
    harmonyActiveColors,
    harmonyHoverColors,
    harmonyPopoverPaper,
} from 'shared/hooks/useHarmonyIconButtonStyles'
import useGlobalSnackbar from 'shared/utils/useGlobalSnackbar'
import ChevronDownIcon from './ChevronDownIcon'

const FIRM_SWITCHER_PANEL_WIDTH = pxToRem(400)
const FIRM_SWITCHER_PANEL_MAX_HEIGHT = pxToRem(360)
const FIRM_SWITCHER_LISTBOX_ID = 'contextPillFirmListbox'
const FIRM_SWITCHER_OPTION_ID_PREFIX = 'contextPillFirmOption-'
const PILL_LABEL_MAX_WIDTH = '12vw'
const FIRM_SWITCH_ERROR_MESSAGE =
    'There was an error switching to the selected Company. Please verify that you have not been removed from the Company Access List.'
// Single-row MUI MenuItem height (px) used by FixedSizeList for item sizing.
const FIRM_OPTION_HEIGHT = 48
// Max visible list height (px): paper max-height (360px) minus search box (~56px),
// with a few pixels of safety margin.
const FIRM_LIST_HEIGHT = 300

const buildFirmLabel = (firm: CurrentUserFirmsProps) =>
    `(${firm.firmAccountTypeAbbreviation}) - ${firm.firmName}`

const useStyles = makeStyles((theme: Theme) => ({
    pill: {
        height: pxToRem(34),
        padding: `0 ${theme.harmony.spacing.sm} 0 ${theme.harmony.spacing.sm}`,
        // Spacing before this pill comes from responsiveMenusLightOverride's container gap.
        display: 'flex',
        alignItems: 'center',
        gap: theme.harmony.spacing.sm,
        color: theme.harmony.colors.textHeader,
        border: 'none',
        borderRadius: '999px',
        background: 'none',
        cursor: 'pointer',
        transition: 'background 150ms ease',
        '&:hover': harmonyHoverColors(theme),
    },
    pillActive: harmonyActiveColors(theme),
    label: {
        ...theme.harmony.typography.tabLabel,
        maxWidth: PILL_LABEL_MAX_WIDTH,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    chevron: {
        width: pxToRem(14),
        height: pxToRem(14),
        color: theme.harmony.colors.textParagraph,
        flexShrink: 0,
        transition: 'transform 150ms ease',
    },
    chevronOpen: {
        transform: 'rotate(180deg)',
    },
    paper: {
        ...harmonyPopoverPaper(theme),
        marginTop: theme.harmony.spacing.xs,
        width: FIRM_SWITCHER_PANEL_WIDTH,
        maxHeight: FIRM_SWITCHER_PANEL_MAX_HEIGHT,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    searchBoxWrapper: {
        flexShrink: 0,
        backgroundColor: theme.harmony.colors.headerBackground,
        padding: `${pxToRem(8)} ${pxToRem(12)}`,
    },
    searchBox: {
        padding: `${pxToRem(6)} ${pxToRem(10)}`,
        border: `1px solid ${theme.harmony.colors.strokeWeak}`,
        borderRadius: '6px',
        fontSize: pxToRem(14),
    },
    firmOptionLabel: {
        flexGrow: 1,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    harmonyTooltip: {
        backgroundColor: theme.harmony.colors.textHeader,
        color: theme.palette.common.white,
        fontFamily: theme.harmony.typography.tabLabel.fontFamily,
        fontSize: pxToRem(12),
        fontWeight: 400,
        padding: `${pxToRem(6)} ${pxToRem(10)}`,
        borderRadius: pxToRem(6),
        boxShadow: theme.harmony.colors.panelShadow,
        maxWidth: pxToRem(360),
    },
    firmOptionHighlighted: {
        backgroundColor: theme.harmony.colors.activeBg,
    },
}))

interface FirmRowData {
    firms: CurrentUserFirmsProps[]
    highlightedIndex: number
    onSelect: (firm: CurrentUserFirmsProps) => void
    onHighlight: (index: number) => void
    firmOptionHighlightedClass: string
    firmOptionLabelClass: string
    harmonyTooltipClass: string
}

interface FirmRowProps {
    index: number
    style: React.CSSProperties
    data: FirmRowData
}

// Defined at module scope so react-window never remounts items due to an
// inline function reference changing on every parent render.
const FirmRowBase = ({ index, style, data }: FirmRowProps) => {
    const {
        firms,
        highlightedIndex,
        onSelect,
        onHighlight,
        firmOptionHighlightedClass,
        firmOptionLabelClass,
        harmonyTooltipClass,
    } = data
    const firm = firms[index]
    const isHighlighted = index === highlightedIndex

    return (
        <MenuItem
            id={FIRM_SWITCHER_OPTION_ID_PREFIX + index}
            data-testid="contextPillFirmOption"
            role="option"
            aria-selected={isHighlighted}
            className={isHighlighted ? firmOptionHighlightedClass : undefined}
            style={style}
            onMouseEnter={() => onHighlight(index)}
            onClick={() => onSelect(firm)}
        >
            <TruncatedLabel
                text={buildFirmLabel(firm)}
                className={firmOptionLabelClass}
                tooltipClassName={harmonyTooltipClass}
            />
        </MenuItem>
    )
}
const FirmRow = React.memo(FirmRowBase)

export interface ContextPillProps {
    getUserFirms: (firm: CurrentUserFirmsProps) => void
    onFirmSelect: () => void
}

const ContextPill = ({
    getUserFirms,
    onFirmSelect,
}: ContextPillProps) => {
    const classes = useStyles()
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
        null
    )
    const [searchText, setSearchText] = React.useState('')
    const [highlightedIndex, setHighlightedIndex] = React.useState(-1)
    const buttonRef = React.useRef<HTMLButtonElement>(null)
    const listRef = React.useRef<FixedSizeList>(null)
    const { data } = useQuery<CurrentUserFirmsResponse>(CurrentUserFirmsQuery)
    const { showGlobalSnackbar } = useGlobalSnackbar()
    const [setMyActiveFirm] = useMutation(UpdateCurrentUserCurrentFirm, {
        onCompleted: onFirmSelect,
        onError: () => {
            showGlobalSnackbar({
                variables: {
                    message: FIRM_SWITCH_ERROR_MESSAGE,
                    variant: 'error',
                    id: 'FirmSwitching_OnSelectionError',
                },
            })
        },
    })

    const handleSelect = React.useCallback(
        (firm: CurrentUserFirmsProps) => {
            setAnchorEl(null)
            getUserFirms(firm)
            setMyActiveFirm({
                variables: {
                    input: {
                        firmId: firm.firmId,
                        activeUrl: firm.firmAccountTypeApplicationURL,
                    },
                },
            })
        },
        [getUserFirms, setMyActiveFirm]
    )

    const handleClose = () => {
        setAnchorEl(null)
        setSearchText('')
    }

    const firmMemberships = data?.myFirmMemberships ?? []

    // Matches the classic UserFirmsMenuDropdown's own gate: the switcher only exists at all
    // when the user has other firms to switch to, not just when there's a mode/name to show.
    const switchableFirms = firmMemberships.filter((firm) => !firm.isActive)

    const activeFirm = firmMemberships.find((firm) => firm.isActive)
    const activeFirmLabel = activeFirm ? buildFirmLabel(activeFirm) : ''

    const visibleFirms = switchableFirms.filter((firm) =>
        firm.firmName.toLowerCase().includes(searchText.trim().toLowerCase())
    )

    // Auto-highlight the first switchable firm whenever the panel opens or the
    // filtered list changes, so "type then Enter" always picks the top match.
    React.useEffect(() => {
        setHighlightedIndex(visibleFirms.length ? 0 : -1)
    }, [anchorEl, searchText, visibleFirms.length])

    // Scroll the virtual list so the keyboard-highlighted option stays visible.
    // FixedSizeList.scrollToItem is a no-op when the item is already visible.
    React.useEffect(() => {
        if (highlightedIndex >= 0) {
            listRef.current?.scrollToItem(highlightedIndex, 'auto')
        }
    }, [highlightedIndex])

    const itemData = React.useMemo<FirmRowData>(
        () => ({
            firms: visibleFirms,
            highlightedIndex,
            onSelect: handleSelect,
            onHighlight: setHighlightedIndex,
            firmOptionHighlightedClass: classes.firmOptionHighlighted,
            firmOptionLabelClass: classes.firmOptionLabel,
            harmonyTooltipClass: classes.harmonyTooltip,
        }),
        [visibleFirms, highlightedIndex, handleSelect, classes]
    )

    // Grow the list to exactly fit the items up to the maximum visible height.
    // This lets the popover shrink when the filtered set is small.
    const listHeight = Math.min(
        visibleFirms.length * FIRM_OPTION_HEIGHT,
        FIRM_LIST_HEIGHT
    )

    const handleSearchKeyDown = (
        event: React.KeyboardEvent<HTMLInputElement>
    ) => {
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault()
                if (visibleFirms.length) {
                    setHighlightedIndex(
                        (previous) => (previous + 1) % visibleFirms.length
                    )
                }
                break
            case 'ArrowUp':
                event.preventDefault()
                if (visibleFirms.length) {
                    setHighlightedIndex(
                        (previous) =>
                            (previous - 1 + visibleFirms.length) %
                            visibleFirms.length
                    )
                }
                break
            case 'Enter':
                event.preventDefault()
                if (
                    highlightedIndex >= 0 &&
                    highlightedIndex < visibleFirms.length
                ) {
                    handleSelect(visibleFirms[highlightedIndex])
                }
                break
            case 'Escape':
                event.preventDefault()
                handleClose()
                buttonRef.current?.focus()
                break
            case 'Home':
                event.preventDefault()
                setHighlightedIndex(visibleFirms.length ? 0 : -1)
                break
            case 'End':
                event.preventDefault()
                setHighlightedIndex(
                    visibleFirms.length ? visibleFirms.length - 1 : -1
                )
                break
            default:
                break
        }
        // Keep keys (Up/Down/Enter/etc.) from leaking to MUI's MenuList,
        // which would otherwise trigger its own focus/type-ahead handling.
        event.stopPropagation()
    }

    if (switchableFirms.length === 0) {
        return null
    }

    return (
        <>
            <button
                type="button"
                ref={buttonRef}
                data-testid="contextPill"
                className={clsx(classes.pill, anchorEl && classes.pillActive)}
                onClick={(event) => setAnchorEl(event.currentTarget)}
                aria-haspopup="listbox"
                aria-expanded={Boolean(anchorEl)}
                aria-controls={anchorEl ? FIRM_SWITCHER_LISTBOX_ID : undefined}
                aria-label={
                    activeFirmLabel
                        ? `Current company ${activeFirmLabel}. Activate to switch company.`
                        : 'Current company. Activate to switch company.'
                }
            >
                <TruncatedLabel
                    text={activeFirmLabel}
                    className={classes.label}
                    tooltipClassName={classes.harmonyTooltip}
                />
                <ChevronDownIcon
                    className={clsx(
                        classes.chevron,
                        anchorEl && classes.chevronOpen
                    )}
                />
            </button>
            <Popover
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                classes={{ paper: classes.paper }}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <div className={classes.searchBoxWrapper}>
                    <InputBase
                        fullWidth
                        autoFocus
                        placeholder="Search Companies"
                        value={searchText}
                        onChange={(event) => setSearchText(event.target.value)}
                        onKeyDown={handleSearchKeyDown}
                        inputProps={{
                            'data-testid': 'contextPillSearchInput',
                            role: 'combobox',
                            'aria-expanded': Boolean(anchorEl),
                            'aria-controls': FIRM_SWITCHER_LISTBOX_ID,
                            'aria-autocomplete': 'list',
                            'aria-activedescendant':
                                highlightedIndex >= 0
                                    ? FIRM_SWITCHER_OPTION_ID_PREFIX +
                                    highlightedIndex
                                    : undefined,
                            'aria-label': 'Search companies',
                        }}
                        className={classes.searchBox}
                    />
                </div>
                <div
                    id={FIRM_SWITCHER_LISTBOX_ID}
                    role="listbox"
                    aria-label="Companies"
                >
                    {visibleFirms.length === 0 ? (
                        <MenuItem
                            disabled
                            role="option"
                            data-testid="contextPillNoResults"
                        >
                            No companies found
                        </MenuItem>
                    ) : (
                        <FixedSizeList
                            ref={listRef}
                            height={listHeight}
                            width="100%"
                            itemCount={visibleFirms.length}
                            itemSize={FIRM_OPTION_HEIGHT}
                            itemData={itemData}
                        >
                            {FirmRow}
                        </FixedSizeList>
                    )}
                </div>
            </Popover>
        </>
    )
}

export default ContextPill
